import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { getImageUrl, uploadImageHelper } from "@/lib/api";
import { getStoredProjects, saveStoredProjects } from "@/lib/projectsData";
import { GalleryEditor } from "./admin/GalleryEditor";

const EMPTY = { title: "", slug: "", sector: "Hotelería", location: "", year: "", description: "", services: "", hero_image: "", gallery: "", featured: true, order: 0 };
const SECTORS = ["Hotelería", "Centros Comerciales", "Viviendas"];

const slugify = (s) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const uploadImage = uploadImageHelper;

const ProjectForm = ({ initial, onSaved, onCancel }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const galleryList = form.gallery ? (Array.isArray(form.gallery) ? form.gallery : form.gallery.split("\n").map((s) => s.trim()).filter(Boolean)) : [];
  const setGalleryList = (list) => set("gallery", list.join("\n"));

  const setAsMain = (url) => {
    set("hero_image", url);
    toast.success("Imagen principal actualizada");
  };

  const onHeroFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHero(true);
    try {
      const url = await uploadImage(file);
      set("hero_image", url);
      toast.success("Imagen principal cargada correctamente");
    } catch (err) {
      toast.error("Para añadir imágenes en hosting estático, introduce la URL directa en el campo de texto inferior.");
    } finally {
      setUploadingHero(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      id: form.id || String(Date.now()),
      slug: form.slug || slugify(form.title),
      services: typeof form.services === "string" ? form.services.split(",").map((s) => s.trim()).filter(Boolean) : form.services,
      gallery: galleryList,
      order: Number(form.order) || 0,
    };
    try {
      if (form.id) await api.put(`/projects/${form.id}`, payload);
      else await api.post("/projects", payload);
      toast.success(form.id ? "Obra actualizada" : "Obra creada");
      onSaved();
    } catch {
      // Fallback for static hosting without active backend API
      const currentProjects = getStoredProjects();
      let updated;
      if (form.id) {
        updated = currentProjects.map((p) => (p.id === form.id || p.slug === form.slug ? payload : p));
      } else {
        updated = [payload, ...currentProjects];
      }
      saveStoredProjects(updated);
      toast.success(form.id ? "Obra actualizada en el navegador" : "Obra creada en el navegador");
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6 border-t border-[#111111] pt-8" data-testid="admin-project-form">
      <div className="grid md:grid-cols-2 gap-6">
        <input data-testid="project-form-title" className="ed-input" placeholder="Título" required value={form.title} onChange={(e) => set("title", e.target.value)} />
        <input data-testid="project-form-slug" className="ed-input" placeholder="Slug (auto)" value={form.slug} onChange={(e) => set("slug", e.target.value)} />
        <select data-testid="project-form-sector" className="ed-input" value={form.sector} onChange={(e) => set("sector", e.target.value)}>
          {SECTORS.map((s) => <option key={s}>{s}</option>)}
        </select>
        <input data-testid="project-form-location" className="ed-input" placeholder="Ubicación" required value={form.location} onChange={(e) => set("location", e.target.value)} />
        <input data-testid="project-form-year" className="ed-input" placeholder="Año" required value={form.year} onChange={(e) => set("year", e.target.value)} />
        <input data-testid="project-form-order" className="ed-input" type="number" placeholder="Orden" value={form.order} onChange={(e) => set("order", e.target.value)} />
      </div>
      <textarea data-testid="project-form-description" className="ed-input" rows={4} placeholder="Descripción" required value={form.description} onChange={(e) => set("description", e.target.value)} />
      <input data-testid="project-form-services" className="ed-input" placeholder="Servicios (separados por coma)" value={form.services} onChange={(e) => set("services", e.target.value)} />
      <div className="space-y-3">
        <p className="text-xs uppercase tracking-widest font-semibold">Imagen principal</p>
        <div className="flex flex-wrap items-center gap-6">
          <label className="cursor-pointer text-[#D94A1E] uppercase tracking-wide text-sm font-medium hover:translate-x-1 transition-transform">
            {uploadingHero ? "Subiendo…" : "Subir imagen"}
            <input data-testid="project-form-hero-file" type="file" accept="image/*" className="hidden" onChange={onHeroFile} disabled={uploadingHero} />
          </label>
          {form.hero_image && <img src={getImageUrl(form.hero_image)} alt="Vista previa" className="h-16 aspect-square object-cover" data-testid="project-form-hero-preview" />}
        </div>
        <input data-testid="project-form-hero" className="ed-input" placeholder="URL imagen principal" required value={form.hero_image} onChange={(e) => set("hero_image", e.target.value)} />
        <p className="text-xs font-light text-[#111111]/50">Las imágenes se redimensionan a un máximo de 3000px de ancho y se convierten a WebP automáticamente.</p>
      </div>
      <GalleryEditor
        gallery={galleryList}
        onChange={setGalleryList}
        mainImage={form.hero_image}
        onSetMain={setAsMain}
      />
      <label className="flex items-center gap-3 text-sm font-light">
        <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#D94A1E]" data-testid="project-form-featured" />
        Destacada en la home
      </label>
      <div className="flex gap-8">
        <button type="submit" disabled={saving || uploadingHero} data-testid="project-form-save" className="text-[#D94A1E] uppercase tracking-wide font-medium hover:translate-x-1 transition-transform disabled:opacity-50">
          {saving ? "Guardando…" : "Guardar"}
        </button>
        <button type="button" onClick={onCancel} data-testid="project-form-cancel" className="uppercase tracking-wide font-light text-[#111111]/60 hover:text-[#111111]">
          Cancelar
        </button>
      </div>
    </form>
  );
};

export default function Admin() {
  const [tab, setTab] = useState("obras");
  const [projects, setProjects] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [checked, setChecked] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [p, m] = await Promise.all([api.get("/projects"), api.get("/contact")]);
      setProjects(Array.isArray(p.data) && p.data.length ? p.data : getStoredProjects());
      setMessages(Array.isArray(m.data) ? m.data : []);
    } catch {
      setProjects(getStoredProjects());
      setMessages([]);
    }
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then(() => { setChecked(true); load(); })
      .catch(() => {
        if (localStorage.getItem("admin_auth") === "true") {
          setChecked(true);
          load();
        } else {
          navigate("/admin/login");
        }
      });
  }, [load, navigate]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta obra?")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Obra eliminada");
    } catch {
      const updated = getStoredProjects().filter((p) => p.id !== id && p.slug !== id);
      saveStoredProjects(updated);
      toast.success("Obra eliminada");
    }
    load();
  };

  const logout = async () => {
    localStorage.removeItem("admin_auth");
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (!checked) return <div className="bg-[#F3F0EA] min-h-screen" />;

  return (
    <div className="bg-[#F3F0EA] text-[#111111] min-h-screen px-6 md:px-10 lg:px-16 py-10" data-testid="admin-dashboard">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <Link to="/" aria-label="Sebastián Ferradás Rosso — Arquitecto" className="inline-block">
          <img src="/logo-sfr.png" alt="Sebastián Ferradás Rosso · Arquitecto" className="w-[110px] max-w-[110px] h-auto object-contain select-none" draggable="false" />
        </Link>
        <div className="flex gap-8 items-center">
          <button data-testid="admin-tab-obras" onClick={() => setTab("obras")} className={`text-sm uppercase tracking-widest ${tab === "obras" ? "text-[#D94A1E] font-semibold" : "font-light"}`}>Obras</button>
          <button data-testid="admin-tab-mensajes" onClick={() => setTab("mensajes")} className={`text-sm uppercase tracking-widest ${tab === "mensajes" ? "text-[#D94A1E] font-semibold" : "font-light"}`}>Mensajes ({messages.length})</button>
          <button data-testid="admin-logout" onClick={logout} className="text-sm uppercase tracking-widest font-light text-[#111111]/60 hover:text-[#D94A1E]">Salir</button>
        </div>
      </header>
      <div className="border-t border-[#111111] mt-6 mb-10" />

      {tab === "obras" && (
        <section>
          <div className="flex items-end justify-between mb-10">
            <h1 className="text-4xl md:text-6xl uppercase tracking-tight font-medium">Obras</h1>
            {!editing && (
              <button data-testid="admin-new-project" onClick={() => setEditing({ ...EMPTY })} className="text-[#D94A1E] uppercase tracking-wide font-medium text-sm hover:translate-x-1 transition-transform">
                + Nueva Obra
              </button>
            )}
          </div>

          {editing ? (
            <ProjectForm
              initial={editing}
              onSaved={() => { setEditing(null); load(); }}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div>
              {projects.map((p) => (
                <div key={p.id} className="border-t border-[#111111] py-5 grid grid-cols-12 gap-4 items-center" data-testid={`admin-project-row-${p.slug}`}>
                  <img src={getImageUrl(p.hero_image)} alt={p.title} className="col-span-3 md:col-span-1 aspect-square object-cover" />
                  <div className="col-span-9 md:col-span-5">
                    <p className="font-medium uppercase tracking-tight text-lg">{p.title}</p>
                    <p className="text-sm font-light text-[#111111]/60">{p.sector} · {p.location} · {p.year}</p>
                  </div>
                  <div className="col-span-12 md:col-span-6 flex md:justify-end gap-6 text-sm uppercase tracking-widest">
                    <button
                      data-testid={`admin-edit-${p.slug}`}
                      onClick={() => setEditing({ ...p, services: p.services.join(", "), gallery: p.gallery.join("\n") })}
                      className="text-[#D94A1E] font-medium"
                    >
                      Editar
                    </button>
                    <button data-testid={`admin-delete-${p.slug}`} onClick={() => remove(p.id)} className="font-light text-[#111111]/60 hover:text-[#D94A1E]">
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
              <div className="border-t border-[#111111]" />
            </div>
          )}
        </section>
      )}

      {tab === "mensajes" && (
        <section data-testid="admin-messages-list">
          <h1 className="text-4xl md:text-6xl uppercase tracking-tight font-medium mb-10">Mensajes</h1>
          {messages.length === 0 && <p className="font-light text-[#111111]/60">No hay mensajes todavía.</p>}
          {messages.map((m) => (
            <div key={m.id} className="border-t border-[#111111] py-6 grid md:grid-cols-12 gap-4" data-testid={`admin-message-${m.id}`}>
              <div className="md:col-span-3">
                <p className="font-medium">{m.name}</p>
                <p className="text-sm font-light text-[#D94A1E]">{m.email}</p>
                <p className="text-xs font-light text-[#111111]/50 mt-1">{new Date(m.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}</p>
              </div>
              <p className="md:col-span-8 md:col-start-5 font-light leading-relaxed">{m.message}</p>
            </div>
          ))}
          {messages.length > 0 && <div className="border-t border-[#111111]" />}
        </section>
      )}
    </div>
  );
}
